import { CalendarClock, Link2, Pause, Play, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { AdminPage } from '@/components/admin/AdminPage';
import { Button } from '@/components/ui/button';
import { DataTable, type Column } from '@/components/ui/data-table';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { StatusBadge } from '@/components/ui/status-badge';
import { useCalendarFeeds, useFeedEvents, useSyncFeed, useUpdateFeed } from '@/hooks/calendar-sync';
import { ApiError } from '@/lib/api';
import { formatDay, formatInstant } from '@/lib/dates';
import { useToast } from '@/lib/toast-context';
import { FeedDialog } from '@/pages/admin/calendar-sync/FeedDialog';
import type { CalendarFeedView, SyncOutcome } from '@shared/schemas/calendar-sync';

/*
 * /admin/calendar-sync — ROADMAP.md §11.7, the prototype's screen rebuilt.
 *
 * The numbered explainer above the empty state is the prototype's own good
 * idea, kept: this feature needs three things from the admin before it does
 * anything, and a screen that lists them beats one that waits silently
 * (FINDINGS.md §2, design-system §7 — explain, don't hide).
 *
 * Sync status is worded, never the raw value (P5), and a feed's error is
 * shown on the feed — it landed there so the office can read it, which is §6
 * rule five working as intended.
 */

const EXPLAINER_STEPS = [
  {
    title: 'Get the calendar link from the listing',
    detail:
      'Airbnb and VRBO both export an iCal link — on Airbnb it is under Calendar → Availability → Connect to another website.',
  },
  {
    title: 'Add the feed here',
    detail:
      'Paste the link, pick which property it cleans, and say what a turnover is on the rate card.',
  },
  {
    title: 'Checkouts become cleans',
    detail:
      'Bookings import every four hours. Each checkout day gets a turnover clean for the office to staff, and cancelled or moved bookings adjust their clean and tell you.',
  },
] as const;

export function CalendarSyncPage() {
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<CalendarFeedView | null>(null);
  const [selectedFeedId, setSelectedFeedId] = useState<string | null>(null);

  const feeds = useCalendarFeeds();
  const feedList = feeds.data?.data ?? [];
  const selectedFeed = feedList.find((feed) => feed.id === selectedFeedId) ?? null;

  return (
    <AdminPage
      title="Calendar Sync"
      description="iCal feeds from short-term rental platforms, so turnovers appear without being typed."
      actions={
        <Button
          onClick={() => {
            setCreating(true);
          }}
        >
          <Link2 aria-hidden="true" />
          Add a feed
        </Button>
      }
    >
      {feeds.isError ? (
        <ErrorState
          title="The feeds could not be loaded"
          onRetry={() => {
            void feeds.refetch();
          }}
        />
      ) : feeds.isPending ? (
        <div aria-hidden="true" className="space-y-3">
          <div className="h-28 animate-pulse rounded-md bg-ink-100" />
          <div className="h-28 animate-pulse rounded-md bg-ink-100" />
        </div>
      ) : feedList.length === 0 ? (
        <>
          {/* The prototype's numbered onboarding, above the empty state. */}
          <ol className="mb-6 grid gap-4 sm:grid-cols-3">
            {EXPLAINER_STEPS.map((step, index) => (
              <li key={step.title} className="rounded-md border border-ink-300 bg-white p-4">
                <span
                  aria-hidden="true"
                  className="grid size-8 place-items-center rounded-full bg-honey-400 text-label font-semibold text-ink-900"
                >
                  {index + 1}
                </span>
                <p className="mt-3 text-label font-medium text-ink-900">
                  <span className="sr-only">Step {index + 1}: </span>
                  {step.title}
                </p>
                <p className="mt-1 text-body text-ink-500">{step.detail}</p>
              </li>
            ))}
          </ol>

          <EmptyState
            icon={CalendarClock}
            title="No calendar feeds yet."
            hint="Add a listing's iCal link and its checkouts start scheduling their own turnover cleans."
            action={
              <Button
                onClick={() => {
                  setCreating(true);
                }}
              >
                Add a feed
              </Button>
            }
          />
        </>
      ) : (
        <div className="space-y-3">
          {feedList.map((feed) => (
            <FeedCard
              key={feed.id}
              feed={feed}
              isSelected={feed.id === selectedFeedId}
              onEdit={() => {
                setEditing(feed);
              }}
              onToggleBookings={() => {
                setSelectedFeedId((current) => (current === feed.id ? null : feed.id));
              }}
            />
          ))}
        </div>
      )}

      {selectedFeed ? <FeedEvents feed={selectedFeed} /> : null}

      <FeedDialog
        key={creating ? 'new' : (editing?.id ?? 'closed')}
        open={creating || editing !== null}
        feed={editing ?? undefined}
        onOpenChange={(next) => {
          if (!next) {
            setCreating(false);
            setEditing(null);
          }
        }}
      />
    </AdminPage>
  );
}

function FeedCard({
  feed,
  isSelected,
  onEdit,
  onToggleBookings,
}: {
  feed: CalendarFeedView;
  isSelected: boolean;
  onEdit: () => void;
  onToggleBookings: () => void;
}) {
  const toast = useToast();
  const syncNow = useSyncFeed(feed.id);
  const setActive = useUpdateFeed(feed.id);

  function describe(outcome: SyncOutcome): string {
    if (outcome.status === 'error') {
      return outcome.error ?? 'The sync failed — the feed shows why.';
    }
    const parts = [
      `${String(outcome.events ?? 0)} booking${outcome.events === 1 ? '' : 's'}`,
      `${String(outcome.created ?? 0)} clean${outcome.created === 1 ? '' : 's'} created`,
    ];
    if ((outcome.rescheduled ?? 0) > 0) parts.push(`${String(outcome.rescheduled)} moved`);
    if ((outcome.cancelled ?? 0) > 0) parts.push(`${String(outcome.cancelled)} cancelled`);
    return parts.join(', ') + '.';
  }

  return (
    <section
      aria-label={feed.name}
      className="rounded-md border border-ink-300 bg-white p-4 sm:p-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-h3 text-ink-900">{feed.name}</h2>
            {feed.isActive ? null : <StatusBadge tone="cancelled" label="Paused" />}
          </div>
          <p className="mt-0.5 truncate text-body text-ink-700">
            {feed.clientName}
            {feed.propertyLabel === null
              ? ' — no property linked, so bookings import but no cleans are created'
              : ` — ${feed.propertyLabel}`}
          </p>
          <p className="text-meta text-ink-500">Turnovers billed as {feed.serviceTypeName}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" size="sm" onClick={onEdit}>
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={setActive.isPending}
            onClick={() => {
              setActive.mutate(
                { isActive: !feed.isActive },
                {
                  onSuccess: (saved) => {
                    toast.success(
                      saved.isActive
                        ? `${saved.name} is syncing again.`
                        : 'Paused. Cleans already created are untouched.',
                    );
                  },
                  onError: (error) => {
                    toast.error(
                      error instanceof ApiError ? error.message : 'That could not be saved.',
                    );
                  },
                },
              );
            }}
          >
            {feed.isActive ? (
              <>
                <Pause aria-hidden="true" />
                Pause
              </>
            ) : (
              <>
                <Play aria-hidden="true" />
                Resume
              </>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={syncNow.isPending}
            onClick={() => {
              syncNow.mutate(undefined, {
                onSuccess: (outcome) => {
                  if (outcome.status === 'ok') toast.success(describe(outcome));
                  else toast.error(describe(outcome));
                },
                onError: (error) => {
                  toast.error(
                    error instanceof ApiError ? error.message : 'The sync could not be run.',
                  );
                },
              });
            }}
          >
            <RefreshCw aria-hidden="true" />
            {syncNow.isPending ? 'Syncing…' : 'Sync now'}
          </Button>
          <Button variant="outline" size="sm" onClick={onToggleBookings}>
            {isSelected ? 'Hide bookings' : 'View bookings'}
          </Button>
        </div>
      </div>

      <SyncStatusLine feed={feed} />
    </section>
  );
}

/** Worded, never the raw `ok`/`error` (P5). The error text is §6's rule five. */
function SyncStatusLine({ feed }: { feed: CalendarFeedView }) {
  if (feed.lastSyncedAt === null) {
    return (
      <p className="mt-3 border-t border-ink-100 pt-3 text-meta text-ink-500">
        Never synced — the worker runs every four hours, or press Sync now.
      </p>
    );
  }

  if (feed.lastSyncStatus === 'error') {
    return (
      <p className="mt-3 border-t border-ink-100 pt-3 text-meta text-status-priority-text">
        Last sync failed, {formatInstant(feed.lastSyncedAt)} —{' '}
        {feed.lastSyncError ?? 'no detail was recorded'}. It is retried every four hours.
      </p>
    );
  }

  return (
    <p className="mt-3 border-t border-ink-100 pt-3 text-meta text-ink-500">
      Synced {formatInstant(feed.lastSyncedAt)} —{' '}
      {/* Zero is `0` (P1): an empty calendar is a fact worth stating. */}
      {String(feed.lastSyncEventCount ?? 0)} booking
      {feed.lastSyncEventCount === 1 ? '' : 's'} on the calendar.
    </p>
  );
}

function FeedEvents({ feed }: { feed: CalendarFeedView }) {
  const events = useFeedEvents(feed.id);

  const columns: Column<NonNullable<typeof events.data>['data'][number]>[] = [
    {
      key: 'booking',
      header: 'Booking',
      render: (event) => (
        <div className="min-w-0">
          <p className="truncate text-body font-medium text-ink-900">
            {event.summary ?? 'Reserved'}
          </p>
          <p className="text-meta text-ink-500">
            {formatDay(event.startsOn)} → checkout {formatDay(event.endsOn)}
          </p>
        </div>
      ),
    },
    {
      key: 'clean',
      header: 'Turnover clean',
      render: (event) =>
        event.createdJobId === null ? (
          <span className="text-meta text-ink-500">
            {feed.propertyLabel === null
              ? 'None — the feed has no property'
              : 'None — checkout already past, or the day already has its clean'}
          </span>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/admin/jobs/${event.createdJobId}`}
              className="text-body text-bark-700 underline underline-offset-4 hover:text-bark-900"
            >
              {formatDay(event.endsOn)}
            </Link>
            {event.createdJobStatusLabel === null ? null : (
              <span className="text-meta text-ink-500">{event.createdJobStatusLabel}</span>
            )}
          </div>
        ),
    },
    {
      key: 'seen',
      header: 'Last seen in the feed',
      render: (event) => (
        <span className="text-meta text-ink-500">{formatInstant(event.lastSeenAt)}</span>
      ),
    },
  ];

  return (
    <section aria-label={`Bookings from ${feed.name}`} className="mt-6">
      <h2 className="mb-3 text-h3 text-ink-900">Bookings from {feed.name}</h2>
      <DataTable
        caption={`Bookings imported from ${feed.name}, with the turnover clean each one created`}
        columns={columns}
        rows={events.data?.data ?? []}
        rowKey={(event) => event.id}
        isLoading={events.isPending}
        error={events.error}
        onRetry={() => {
          void events.refetch();
        }}
        empty={
          <EmptyState
            icon={CalendarClock}
            title="No bookings imported yet."
            hint="Press Sync now, or wait for the four-hour cycle."
          />
        }
      />
    </section>
  );
}
